import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSButton from "views/system-design/DSButton";
import DSTable from "views/system-design/Table/DSTable";
import DSTableHead from "views/system-design/Table/DSTableHead";
import DSTableBody from "views/system-design/Table/DSTableBody";
import DSTableRow from "views/system-design/Table/DSTableRow";
import DSTableCell from "views/system-design/Table/DSTableCell";
import DSTableContainer from "views/system-design/Table/DSTableContainer";
import DSSegmentedTabs from "views/system-design/DSSegmentedTabs";
import PageHeader from "components/PageHeader";
import useTabParam from "components/hooks/useTabParam";
import { KlydoMonitor, MonitorType } from "../../api/admin";
import {
  useHasUnacknowledgedMonitorFailures,
  useKlydoMonitors,
} from "./hooks/useKlydoMonitors";
import { useAlertPolicies } from "views/AlertPolicies/hooks/useAlertPolicies";
import MonitorStatusChip from "./components/MonitorStatusChip";
import MonitorDetailsModal from "./components/MonitorDetailsModal";
import CreateMonitorModal from "./components/CreateMonitorModal";
import MonitorFilters, {
  MonitorFiltersState,
  loadStoredMonitorFilters,
  matchesMonitorFilters,
  pruneMonitorFilters,
  storeMonitorFilters,
} from "./components/MonitorFilters";
import ClocksMonitor from "views/Monitor/Component/ClocksMonitor";
import AlertPolicies from "views/AlertPolicies/components/AlertPolicies";
import SentryIssues from "views/SentryIssues/components/SentryIssues";
import {
  markSentryIssuesVisited,
  NEW_SENTRY_ISSUES_QUERY_KEY,
  useHasNewSentryIssues,
} from "views/SentryIssues/hooks/useNewSentryIssues";

const formatTime = (ts: number | null | undefined) => {
  if (!ts) return "\u2014";
  return new Date(ts).toLocaleString();
};

const MonitorsTab = () => {
  const {
    data: monitors,
    isLoading,
    isFetching,
    isError,
    error,
  } = useKlydoMonitors();
  const { data: policies } = useAlertPolicies();
  const [selected, setSelected] = useState<KlydoMonitor | null>(null);
  const [creating, setCreating] = useState(false);
  const [filters, setFilters] = useState<MonitorFiltersState>(
    loadStoredMonitorFilters,
  );
  const queryClient = useQueryClient();

  // Write-through: storage always mirrors the committed state, so any
  // setFilters call site persists without having to remember a second call.
  useEffect(() => {
    storeMonitorFilters(filters);
  }, [filters]);

  // Once policies load, drop stored ids of deleted policies — they have no
  // menu row to untick and would silently filter the table forever.
  useEffect(() => {
    if (!policies) return;
    setFilters((previous) => pruneMonitorFilters(previous, policies));
  }, [policies]);

  const policyNameById = new Map(
    (policies ?? []).map((policy) => [policy.id, policy.name]),
  );
  const filteredMonitors = monitors?.filter((monitor) =>
    matchesMonitorFilters(monitor, filters),
  );

  if (isLoading) {
    return (
      <DSBox sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <DSCircularProgress />
      </DSBox>
    );
  }

  if (isError) {
    return (
      <DSBox sx={{ p: 2 }}>
        <DSTypography color="error">
          Failed to load monitors:{" "}
          {error instanceof Error ? error.message : "Unknown error"}
        </DSTypography>
      </DSBox>
    );
  }

  return (
    <DSBox>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          alignItems: "flex-end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <MonitorFilters
          filters={filters}
          onFiltersChange={setFilters}
          policies={policies ?? []}
        />
        <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isFetching && <DSCircularProgress size={18} />}
          <DSButton
            variant="contained"
            size="small"
            onClick={() => setCreating(true)}
          >
            New monitor
          </DSButton>
          <DSButton
            variant="outlined"
            size="small"
            disabled={isFetching}
            onClick={() =>
              queryClient.invalidateQueries({ queryKey: ["klydo-monitors"] })
            }
          >
            Refresh
          </DSButton>
        </DSBox>
      </DSBox>

      <DSTableContainer>
        <DSTable size="small">
          <DSTableHead>
            <DSTableRow>
              <DSTableCell>Name</DSTableCell>
              <DSTableCell>Status</DSTableCell>
              <DSTableCell>Alert Policy</DSTableCell>
              <DSTableCell>Last Run</DSTableCell>
              <DSTableCell>Last Success</DSTableCell>
              <DSTableCell>Issues</DSTableCell>
              <DSTableCell>Interval</DSTableCell>
              <DSTableCell>Snooze</DSTableCell>
            </DSTableRow>
          </DSTableHead>
          <DSTableBody>
            {filteredMonitors?.length === 0 && (
              <DSTableRow>
                <DSTableCell colSpan={8} align="center">
                  <DSTypography variant="caption" color="text.secondary">
                    {monitors?.length
                      ? "No monitors match the current filters"
                      : "No monitors"}
                  </DSTypography>
                </DSTableCell>
              </DSTableRow>
            )}
            {filteredMonitors?.map((monitor) => {
              const isSnoozed =
                monitor.snoozeUntil !== null &&
                Date.now() < monitor.snoozeUntil;
              // `active` defaults to true server-side; only an explicit false
              // means paused (older docs may omit the field entirely).
              const isPaused = monitor.active === false;
              const isClockInternal =
                monitor.type === MonitorType.ClockInternal;
              // Clock-internal monitors carry no `issues[]`; their failure
              // signal is the accumulated failureReports.
              const issueCount = isClockInternal
                ? (monitor.failureReports?.length ?? 0)
                : (monitor.issues?.length ?? 0);

              return (
                <DSTableRow
                  key={monitor.id}
                  hover
                  sx={{ cursor: "pointer", opacity: isPaused ? 0.5 : 1 }}
                  onClick={() => setSelected(monitor)}
                >
                  <DSTableCell>
                    <DSBox
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <DSTypography variant="body2" fontWeight={500}>
                        {monitor.name}
                      </DSTypography>
                      {isClockInternal && (
                        <DSTypography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            px: 0.5,
                          }}
                        >
                          clock
                        </DSTypography>
                      )}
                    </DSBox>
                  </DSTableCell>
                  <DSTableCell>
                    <DSBox
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <MonitorStatusChip status={monitor.status} />
                      {isPaused && (
                        <DSTypography variant="caption" color="text.secondary">
                          {"⏸ Paused"}
                        </DSTypography>
                      )}
                      {monitor.acknowledge && (
                        <DSTypography variant="caption" color="warning.main">
                          {"\u{1F590}"}
                        </DSTypography>
                      )}
                    </DSBox>
                  </DSTableCell>
                  <DSTableCell>
                    <DSTypography variant="caption">
                      {monitor.alertPolicyId
                        ? // Fall back to the raw id so a reference to a
                          // deleted policy stays visible.
                          (policyNameById.get(monitor.alertPolicyId) ??
                          monitor.alertPolicyId)
                        : "—"}
                    </DSTypography>
                  </DSTableCell>
                  <DSTableCell>
                    <DSTypography variant="caption">
                      {formatTime(monitor.lastRunAt)}
                    </DSTypography>
                  </DSTableCell>
                  <DSTableCell>
                    <DSTypography variant="caption">
                      {formatTime(monitor.lastSuccessAt)}
                    </DSTypography>
                  </DSTableCell>
                  <DSTableCell>
                    {issueCount > 0 ? (
                      <DSTypography variant="caption" color="error">
                        {issueCount} {isClockInternal ? "report" : "issue"}
                        {issueCount > 1 ? "s" : ""}
                      </DSTypography>
                    ) : (
                      <DSTypography variant="caption" color="success.main">
                        {"\u2713"}
                      </DSTypography>
                    )}
                  </DSTableCell>
                  <DSTableCell>
                    <DSTypography variant="caption">
                      {isClockInternal ? "—" : `${monitor.intervalHours}h`}
                    </DSTypography>
                  </DSTableCell>
                  <DSTableCell>
                    {isSnoozed && (
                      <DSTypography variant="caption" color="text.secondary">
                        Until{" "}
                        {new Date(monitor.snoozeUntil!).toLocaleDateString()}
                      </DSTypography>
                    )}
                  </DSTableCell>
                </DSTableRow>
              );
            })}
          </DSTableBody>
        </DSTable>
      </DSTableContainer>

      <MonitorDetailsModal
        monitor={
          selected
            ? (monitors?.find((m) => m.id === selected.id) ?? selected)
            : null
        }
        open={selected !== null}
        onClose={() => setSelected(null)}
      />

      <CreateMonitorModal open={creating} onClose={() => setCreating(false)} />
    </DSBox>
  );
};

const KlydoMonitorView = () => {
  const [tabParam, setTabParam] = useTabParam("tab", "monitors", [
    "monitors",
    "clock-errors",
    "sentry-issues",
    "alert-policies",
  ]);
  const queryClient = useQueryClient();
  const { data: hasMonitorFailures } = useHasUnacknowledgedMonitorFailures();
  const { data: hasNewSentryIssues } = useHasNewSentryIssues();

  // Opening the tab is what counts as having looked: stamp the visit so the
  // dot only comes back for issues that arrive after this moment.
  useEffect(() => {
    if (tabParam !== "sentry-issues") return;
    markSentryIssuesVisited();
    queryClient.invalidateQueries({ queryKey: NEW_SENTRY_ISSUES_QUERY_KEY });
  }, [tabParam, queryClient]);

  return (
    <DSBox>
      <PageHeader
        overline="Devices"
        title="Klydo Monitors"
        subtitle="Automated health checks and clock error reports for the fleet"
        tabs={
          <DSSegmentedTabs
            options={[
              {
                label: "Monitors",
                value: "monitors",
                badge: !!hasMonitorFailures,
              },
              { label: "Clock Errors", value: "clock-errors" },
              {
                label: "Sentry Issues",
                value: "sentry-issues",
                badge: !!hasNewSentryIssues,
              },
              { label: "Alert Policies", value: "alert-policies" },
            ]}
            value={tabParam ?? "monitors"}
            onChange={(value) => setTabParam(value)}
          />
        }
      />
      {tabParam === "monitors" && <MonitorsTab />}
      {tabParam === "clock-errors" && <ClocksMonitor />}
      {tabParam === "sentry-issues" && <SentryIssues />}
      {tabParam === "alert-policies" && <AlertPolicies />}
    </DSBox>
  );
};

export default KlydoMonitorView;
